import React, { useState, useEffect } from 'react';
import { type UserVideo, extendVideo, type VideoExtensionRequest } from '@/api/videoApi';
import { UserImage } from '@/api/imageApi';
import { Download, Delete, Reply, VideoLibrary } from '@mui/icons-material';
import DeleteConfirmModal from '../modals/DeleteConfirmModal';
import {
  createRedHoverHandlers,
  createGrayHoverHandlers,
  createDarkGrayHoverHandlers,
  getVideoTypeLabel,
  copyToClipboard,
  buttonStyles,
  createTitleSaveHandler,
} from '@/utils/common/modal/modalUtils';
import {
  renderContentPreview,
  getDeleteButtonTitle,
  getDeleteConfirmationMessage,
  handleAdminDelete,
  handleVideoDeleteRequest,
  handleImageDeleteRequest,
  handleDownloadVideoFrame,
  handleDownloadImage,
  handleDownloadVideo as utilsHandleDownloadVideo,
} from '@/utils/dashboard/videoDetailsUtils';
import { renderPromptInfo, renderContentInfo, renderContentSpecificInfo, renderLoadingSpinner, renderModalHeader, renderMobileBadges } from '@/utils/common/modal/contentRenderUtils';
import { useUpdateVideoMetadata } from '@/hooks/querry_hooks/dashboard/useVideoMutations';
import { useUpdateImageMetadata } from '@/hooks/querry_hooks/dashboard/useImageMutations';

// Helper hook to manage content details state
const useContentDetails = (currentContent: UserVideo | UserImage | null | undefined, isImageContent: boolean) => {
  const [isDownloadingFrame, setIsDownloadingFrame] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const [isExtendingVideo, setIsExtendingVideo] = useState(false);
  const [extensionDuration, setExtensionDuration] = useState(150); // Default: 2:30 minutes
  const [isRequestingDelete, setIsRequestingDelete] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [hasDeleteRequest, setHasDeleteRequest] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  useEffect(() => {
    setHasDeleteRequest(currentContent?.delete_request || false);

    // Use the actual title from the content
    let initialTitle = currentContent?.title || '';
    setEditedTitle(initialTitle);
    setIsEditingTitle(false);
  }, [currentContent, isImageContent]);

  return {
    isDownloadingFrame,
    setIsDownloadingFrame,
    isDownloadingVideo,
    setIsDownloadingVideo,
    isExtendingVideo,
    setIsExtendingVideo,
    extensionDuration,
    setExtensionDuration,
    isRequestingDelete,
    setIsRequestingDelete,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showCopiedMessage,
    setShowCopiedMessage,
    hasDeleteRequest,
    setHasDeleteRequest,
    isEditingTitle,
    setIsEditingTitle,
    editedTitle,
    setEditedTitle,
  };
};

// Helper function to handle frame/image download
const createDownloadFrameHandler = (
  isVideoContent: boolean,
  isImageContent: boolean,
  selectedVideo: UserVideo | null | undefined,
  selectedImage: UserImage | null | undefined,
  onDownloadLastFrame: ((signedUrl: string) => Promise<void>) | undefined,
  setIsDownloadingFrame: React.Dispatch<React.SetStateAction<boolean>>
) => async () => {
  setIsDownloadingFrame(true);
  try {
    if (isVideoContent) {
      await handleDownloadVideoFrame(selectedVideo, onDownloadLastFrame);
    } else if (isImageContent) {
      await handleDownloadImage(selectedImage);
    }
  } finally {
    setIsDownloadingFrame(false);
  }
};

// Helper function to handle video download
const createDownloadVideoHandler = (
  selectedVideo: UserVideo | null | undefined,
  setIsDownloadingVideo: React.Dispatch<React.SetStateAction<boolean>>
) => async () => {
  setIsDownloadingVideo(true);
  try {
    await utilsHandleDownloadVideo(selectedVideo);
  } catch (error) {
    console.error('Error downloading video:', error);
    alert('Failed to download video. Please try again.');
  } finally {
    setIsDownloadingVideo(false);
  }
};

// Helper function to format duration for display
const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes}:00 min`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} min`;
};

// Helper function to handle video extension
const createExtendVideoHandler = (
  selectedVideo: UserVideo | null | undefined,
  setIsExtendingVideo: React.Dispatch<React.SetStateAction<boolean>>,
  extensionDuration: number,
  isAdmin: boolean
) => async () => {
  if (!selectedVideo?.id || !selectedVideo?.prompt) {
    alert('Cannot extend video: Missing video information');
    return;
  }

  setIsExtendingVideo(true);
  try {
    const request: VideoExtensionRequest = {
      video_id: selectedVideo.id,
      prompt: selectedVideo.prompt,
      target_duration: extensionDuration,
    };

    const response = await extendVideo(request);
    
    if (response.success) {
      if (isAdmin) {
        // Admin: task started immediately
        const estimatedMinutes = response.estimated_duration 
          ? Math.ceil(response.estimated_duration / 60) 
          : 5;
        alert(
          `Video extension started!\n` +
          `Estimated time: ${estimatedMinutes} minutes`
        );
      } else {
        // Regular user: request submitted for approval
        alert(
          `Extension request submitted!\n` +
          `Your request is pending admin approval. You will be notified once it is processed.`
        );
      }
    } else {
      alert(`Failed to submit extension request: ${response.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('Error extending video:', error);
    alert('Failed to submit video extension request. Please try again.');
  } finally {
    setIsExtendingVideo(false);
  }
};

// Helper function to create delete handler
const createDeleteHandler = (params: {
  isVideoContent: boolean;
  isImageContent: boolean;
  onDelete: ((videoId: string) => Promise<void>) | undefined;
  onImageDelete: ((imageId: string) => Promise<void>) | undefined;
  selectedVideo: UserVideo | null | undefined;
  selectedImage: UserImage | null | undefined;
  setShowDeleteConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}) => async (e?: React.MouseEvent) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  await handleAdminDelete(
    params.isVideoContent,
    params.isImageContent,
    params.onDelete,
    params.onImageDelete,
    params.selectedVideo,
    params.selectedImage
  );

  params.setShowDeleteConfirm(false);
  params.onClose();
};

// Helper function to create request delete handler
const createRequestDeleteHandler = (params: {
  hasDeleteRequest: boolean;
  isVideoContent: boolean;
  isImageContent: boolean;
  onRequestDelete: ((videoId: string) => Promise<void>) | undefined;
  onRequestImageDelete: ((imageId: string) => Promise<void>) | undefined;
  selectedVideo: UserVideo | null | undefined;
  selectedImage: UserImage | null | undefined;
  setIsRequestingDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setHasDeleteRequest: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDeleteConfirm: React.Dispatch<React.SetStateAction<boolean>>;
}) => async (e?: React.MouseEvent) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (params.hasDeleteRequest) {
    params.setShowDeleteConfirm(false);
    return;
  }

  params.setIsRequestingDelete(true);
  try {
    if (params.isVideoContent) {
      await handleVideoDeleteRequest(params.onRequestDelete, params.selectedVideo, params.setHasDeleteRequest);
    } else if (params.isImageContent) {
      await handleImageDeleteRequest(params.onRequestImageDelete, params.selectedImage, params.setHasDeleteRequest);
    }
    params.setShowDeleteConfirm(false);
  } catch (error) {
    console.error('Failed to request delete:', error);
    alert('Failed to submit delete request. Please try again.');
  } finally {
    params.setIsRequestingDelete(false);
  }
};

// Helper function to standardize content title
const getStandardizedTitle = (
  currentContent: UserVideo | UserImage | null | undefined,
  isVideoContent: boolean,
): string => {
  let contentTitle = currentContent?.title || `[${isVideoContent ? 'Video' : 'Image'}/File Name]`;

  return contentTitle;
};

// Helper to get delete button text
const getDeleteButtonText = (hasDeleteRequest: boolean, isAdmin: boolean): string => {
  if (hasDeleteRequest) return 'Delete Requested';
  return isAdmin ? 'Delete' : 'Request Delete';
};

// Shared props for both mobile and desktop footer button layouts
interface FooterButtonsProps {
  showDeleteConfirm: boolean;
  isVideoContent: boolean;
  isImageContent: boolean;
  selectedVideo: UserVideo | null | undefined;
  selectedImage: UserImage | null | undefined;
  isDownloadingFrame: boolean;
  isDownloadingVideo: boolean;
  isExtendingVideo: boolean;
  isAdmin: boolean;
  hasDeleteRequest: boolean;
  onDownloadLastFrame?: (signedUrl: string) => Promise<void>;
  onDelete?: (videoId: string) => Promise<void>;
  onImageDelete?: (imageId: string) => Promise<void>;
  onRequestDelete?: (videoId: string) => Promise<void>;
  onRequestImageDelete?: (imageId: string) => Promise<void>;
  handleDownloadVideo: () => Promise<void>;
  handleDownloadFrame: () => Promise<void>;
  onClose: () => void;
  setShowDeleteConfirm: (show: boolean) => void;
  onCopyLink: () => void;
  linkCopied: boolean;
}

// Mobile Footer Buttons Component
const MobileFooterButtons: React.FC<FooterButtonsProps> = (props) => {
  const {
    showDeleteConfirm,
    isVideoContent,
    isImageContent,
    selectedVideo,
    selectedImage,
    isDownloadingFrame,
    isDownloadingVideo,
    isExtendingVideo,
    isAdmin,
    hasDeleteRequest,
    onDownloadLastFrame,
    onDelete,
    onImageDelete,
    onRequestDelete,
    onRequestImageDelete,
    handleDownloadVideo,
    handleDownloadFrame,
    onClose,
    setShowDeleteConfirm,
    onCopyLink,
    linkCopied,
  } = props;

  const deleteButtonText = getDeleteButtonText(hasDeleteRequest, isAdmin);

  return (
    <div className="md:hidden flex flex-col gap-3">
      {/* First Row: Download icon + Share icon + Close button */}
      <div className="flex items-center gap-3">
        {!showDeleteConfirm && ((isVideoContent && selectedVideo) || (isImageContent && selectedImage)) && (
          <button
            onClick={isVideoContent ? handleDownloadVideo : handleDownloadFrame}
            className="flex items-center justify-center transition-colors disabled:opacity-50"
            style={{ ...buttonStyles.iconButton, border: '1px solid #d1d5db' }}
            disabled={isDownloadingFrame || isDownloadingVideo}
            title={isVideoContent ? "Download Video" : "Download"}
            {...createGrayHoverHandlers(isDownloadingFrame || isDownloadingVideo)}
          >
            {(isDownloadingVideo || isDownloadingFrame) ? renderLoadingSpinner('gray') : <Download className="w-6 h-6 text-gray-600" />}
          </button>
        )}
        {!showDeleteConfirm && (isVideoContent && selectedVideo) && (
          <button
            onClick={onCopyLink}
            className="flex items-center justify-center transition-colors"
            style={{ ...buttonStyles.iconButton, border: '1px solid #d1d5db' }}
            title={linkCopied ? "Copied!" : "Copy link"}
            {...createGrayHoverHandlers(false)}
          >
            {linkCopied ? (
              <span className="text-gray-600 text-xs font-medium">Copied!</span>
            ) : (
              <Reply className="w-6 h-6 text-gray-600" style={{ transform: 'scaleX(-1)' }} />
            )}
          </button>
        )}
        {!showDeleteConfirm && (
          <button
            onClick={onClose}
            className="text-gray-700 transition-colors flex-1"
            style={buttonStyles.secondaryButton}
            disabled={isDownloadingFrame || isDownloadingVideo}
            {...createGrayHoverHandlers(false)}
          >
            Close
          </button>
        )}
      </div>
      {/* Second Row: Delete icon + Last Frame button */}
      <div className="flex items-center gap-3">
        {!showDeleteConfirm && (isAdmin ? (onDelete || onImageDelete) : (onRequestDelete || onRequestImageDelete)) && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center justify-center transition-colors text-red-600"
            style={{ ...buttonStyles.iconButton, border: '1px solid #fca5a5' }}
            disabled={isDownloadingFrame || isDownloadingVideo}
            title={deleteButtonText}
            {...createRedHoverHandlers()}
          >
            <Delete className="w-6 h-6" />
          </button>
        )}
        {!showDeleteConfirm && isVideoContent && selectedVideo && onDownloadLastFrame && (
          <button
            onClick={handleDownloadFrame}
            className="text-white transition-colors disabled:opacity-50 flex items-center justify-center flex-1"
            style={buttonStyles.primaryButton}
            disabled={isDownloadingFrame || isDownloadingVideo || isExtendingVideo}
            {...createDarkGrayHoverHandlers(isDownloadingFrame || isDownloadingVideo || isExtendingVideo)}
          >
            {isDownloadingFrame ? (
              <>{renderLoadingSpinner('white')}<span>Downloading...</span></>
            ) : (
              <><Download className="w-5 h-5" /><span>Last Frame</span></>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Desktop Footer Buttons Component
const DesktopFooterButtons: React.FC<FooterButtonsProps> = (props) => {
  const {
    showDeleteConfirm,
    isVideoContent,
    isImageContent,
    selectedVideo,
    selectedImage,
    isDownloadingFrame,
    isDownloadingVideo,
    isExtendingVideo,
    isAdmin,
    hasDeleteRequest,
    onDownloadLastFrame,
    onDelete,
    onImageDelete,
    onRequestDelete,
    onRequestImageDelete,
    handleDownloadVideo,
    handleDownloadFrame,
    onClose,
    setShowDeleteConfirm,
    onCopyLink,
    linkCopied,
  } = props;

  return (
    <div className="hidden md:flex items-center justify-between" style={{ gap: '12px' }}>
      {/* Left side - Delete and Download Video icon buttons */}
      <div className="flex items-center" style={{ gap: '12px' }}>
        {!showDeleteConfirm && (isAdmin ? (onDelete || onImageDelete) : (onRequestDelete || onRequestImageDelete)) && (
          <button
            onClick={() => {
              setShowDeleteConfirm(true);
            }}
            className="flex items-center justify-center transition-colors text-red-600"
            style={{
              ...buttonStyles.iconButton,
              border: '1px solid #fca5a5'
            }}
            disabled={isDownloadingFrame || isDownloadingVideo}
            title={getDeleteButtonTitle(isAdmin, hasDeleteRequest)}
            {...createRedHoverHandlers()}
          >
            <Delete className="w-6 h-6" />
          </button>
        )}

        {/* Download Video icon button (for videos only) */}
        {!showDeleteConfirm && isVideoContent && selectedVideo && (
          <button
            onClick={handleDownloadVideo}
            className="flex items-center justify-center transition-colors disabled:opacity-50"
            style={{
              ...buttonStyles.iconButton,
              border: '1px solid #d1d5db'
            }}
            disabled={isDownloadingFrame || isDownloadingVideo}
            title="Download Video"
            {...createGrayHoverHandlers(isDownloadingFrame || isDownloadingVideo)}
          >
            {isDownloadingVideo ? renderLoadingSpinner('gray') : (
              <Download className="w-6 h-6 text-gray-600" />
            )}
          </button>
        )}

        {/* Download button for images */}
        {!showDeleteConfirm && isImageContent && selectedImage && (
          <button
            onClick={handleDownloadFrame}
            className="flex items-center justify-center transition-colors disabled:opacity-50"
            style={{
              ...buttonStyles.iconButton,
              border: '1px solid #d1d5db'
            }}
            disabled={isDownloadingFrame || isDownloadingVideo}
            title="Download"
            {...createGrayHoverHandlers(isDownloadingFrame || isDownloadingVideo)}
          >
            {isDownloadingFrame ? renderLoadingSpinner('gray') : (
              <Download className="w-6 h-6 text-gray-600" />
            )}
          </button>
        )}

        {/* Share button (copy link) — videos only */}
        {!showDeleteConfirm && isVideoContent && selectedVideo && (
          <button
            onClick={onCopyLink}
            className="flex items-center justify-center transition-colors"
            style={{
              ...buttonStyles.iconButton,
              border: '1px solid #d1d5db'
            }}
            title={linkCopied ? "Copied!" : "Copy link"}
            {...createGrayHoverHandlers(false)}
          >
            {linkCopied ? (
              <span className="text-gray-600 text-xs font-medium">Copied!</span>
            ) : (
              <Reply className="w-6 h-6 text-gray-600" style={{ transform: 'scaleX(-1)' }} />
            )}
          </button>
        )}
      </div>

      {/* Right side - Close and Last Frame buttons */}
      <div className="flex items-center" style={{ gap: '12px' }}>
        {!showDeleteConfirm && (
          <>
            <button
              onClick={onClose}
              className="text-gray-700 transition-colors"
              style={buttonStyles.secondaryButton}
              disabled={isDownloadingFrame || isDownloadingVideo}
              {...createGrayHoverHandlers(false)}
            >
              Close
            </button>

            {/* Last Frame button (for videos only) */}
            {isVideoContent && selectedVideo && onDownloadLastFrame && (
              <button
                onClick={handleDownloadFrame}
                className="text-white transition-colors disabled:opacity-50 flex items-center justify-center"
                style={buttonStyles.primaryButton}
                disabled={isDownloadingFrame || isDownloadingVideo || isExtendingVideo}
                {...createDarkGrayHoverHandlers(isDownloadingFrame || isDownloadingVideo || isExtendingVideo)}
              >
                {isDownloadingFrame ? (
                  <>
                    {renderLoadingSpinner('white')}
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Last Frame</span>
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface ContentDetailsModalProps {
  selectedVideo?: UserVideo | null;
  selectedImage?: UserImage | null;
  onClose: () => void;
  formatDate: (dateString: string) => string;
  formatFileSize: (bytes: number) => string;
  onDownloadLastFrame?: (signedUrl: string) => Promise<void>;
  onRequestDelete?: (videoId: string) => Promise<void>;
  onRequestImageDelete?: (imageId: string) => Promise<void>;
  onDelete?: (videoId: string) => Promise<void>;
  onImageDelete?: (imageId: string) => Promise<void>;
  isAdmin: boolean;
  pageValue?: string;
}

const ContentDetailsModal: React.FC<ContentDetailsModalProps> = ({
  selectedVideo,
  selectedImage,
  onClose,
  formatDate,
  formatFileSize,
  onDownloadLastFrame,
  onRequestDelete,
  onRequestImageDelete,
  isAdmin,
  pageValue,
  onDelete,
  onImageDelete,
}) => {
  // React Query mutation hooks
  const updateVideoMutation = useUpdateVideoMetadata();
  const updateImageMutation = useUpdateImageMetadata();

  // Determine which content we're showing
  const isVideoContent = pageValue === 'Videos' || (!pageValue && selectedVideo && !selectedImage);
  const isImageContent = pageValue === 'Images' || (!pageValue && selectedImage && !selectedVideo);
  const currentContent = isVideoContent ? selectedVideo : selectedImage;

  // Use custom hook for state management
  const {
    isDownloadingFrame,
    setIsDownloadingFrame,
    isDownloadingVideo,
    setIsDownloadingVideo,
    isExtendingVideo,
    setIsExtendingVideo,
    extensionDuration,
    setExtensionDuration,
    isRequestingDelete,
    setIsRequestingDelete,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showCopiedMessage,
    setShowCopiedMessage,
    hasDeleteRequest,
    setHasDeleteRequest,
    isEditingTitle,
    setIsEditingTitle,
    editedTitle,
    setEditedTitle,
  } = useContentDetails(currentContent, Boolean(isImageContent));

  // Share / copy-link state — must be before any early return
  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopyLink = () => {
    if (selectedVideo?.id) {
      navigator.clipboard.writeText(globalThis.location.origin + '/dashboard?video=' + selectedVideo.id);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (!currentContent) return null;

  // Create handlers using factory functions
  const handleDownloadFrame = createDownloadFrameHandler(
    Boolean(isVideoContent),
    Boolean(isImageContent),
    selectedVideo,
    selectedImage,
    onDownloadLastFrame,
    setIsDownloadingFrame
  );

  const handleDownloadVideo = createDownloadVideoHandler(selectedVideo, setIsDownloadingVideo);

  const handleExtendVideo = createExtendVideoHandler(selectedVideo, setIsExtendingVideo, extensionDuration, isAdmin);

  const handleDelete = createDeleteHandler({
    isVideoContent: Boolean(isVideoContent),
    isImageContent: Boolean(isImageContent),
    onDelete,
    onImageDelete,
    selectedVideo,
    selectedImage,
    setShowDeleteConfirm,
    onClose,
  });

  const handleRequestDelete = createRequestDeleteHandler({
    hasDeleteRequest,
    isVideoContent: Boolean(isVideoContent),
    isImageContent: Boolean(isImageContent),
    onRequestDelete,
    onRequestImageDelete,
    selectedVideo,
    selectedImage,
    setIsRequestingDelete,
    setHasDeleteRequest,
    setShowDeleteConfirm,
  });

  const contentTitle = getStandardizedTitle(currentContent, Boolean(isVideoContent));
  const videoTypeLabel = getVideoTypeLabel(selectedVideo?.video_type);
  const imageTypeLabel = getVideoTypeLabel(selectedImage?.image_type);

  const handleCopyPrompt = () => {
    if (currentContent.prompt) {
      copyToClipboard(currentContent.prompt, setShowCopiedMessage);
    }
  };

  const saveTitle = createTitleSaveHandler({
    getCurrentTitle: () => currentContent?.title,
    getEditedTitle: () => editedTitle,
    setEditedTitle,
    setIsEditing: setIsEditingTitle,
    onOptimisticUpdate: (trimmedTitle) => {
      // Immediately update the UI
      if (isVideoContent && selectedVideo) {
        selectedVideo.title = trimmedTitle;
      } else if (isImageContent && selectedImage) {
        selectedImage.title = trimmedTitle;
      }
    },
    onSave: async (trimmedTitle) => {
      if (isVideoContent && selectedVideo) {
        await updateVideoMutation.mutateAsync({ videoId: selectedVideo.id, title: trimmedTitle });
      } else if (isImageContent && selectedImage) {
        await updateImageMutation.mutateAsync({ imageId: selectedImage.id, title: trimmedTitle });
      }
    },
  });

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        showDeleteConfirm={showDeleteConfirm}
        isRequestingDelete={isRequestingDelete}
        isAdmin={isAdmin}
        hasDeleteRequest={hasDeleteRequest}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={isAdmin ? handleDelete : handleRequestDelete}
        getDeleteConfirmationMessage={getDeleteConfirmationMessage}
      />

      {/* Main Video/Image Details Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-[766px] max-h-[94vh] overflow-y-auto shadow-2xl">
          {/* Modal Header with Editable Title and Close Button */}
          {renderModalHeader(
            contentTitle,
            isVideoContent ? videoTypeLabel : imageTypeLabel,
            currentContent.status,
            onClose,
            {
              isEditing: isEditingTitle,
              editedValue: editedTitle,
              canEdit: true,
              onStartEdit: () => setIsEditingTitle(true),
              onChange: setEditedTitle,
              onSave: () => {
                saveTitle();
              },
              onCancel: () => {
                setEditedTitle(contentTitle);
                setIsEditingTitle(false);
              },
            }
          )}

          {/* Modal Content */}
          <div className="px-6 pb-6 space-y-4">
            {/* Content Preview */}
            <div
              className={`${isImageContent ? 'w-full max-w-md mx-auto flex items-center justify-center' : 'aspect-video bg-gray-100'} rounded-xl overflow-hidden shadow-md`}
              style={isImageContent ? { maxHeight: '500px' } : {}}
            >
              {renderContentPreview(!!isVideoContent, selectedVideo, !!isImageContent, selectedImage)}
            </div>

            {/* Content Info Grid */}
            <div className="space-y-3">
              {/* Single line for Created, Generated By, Model, Size */}
              {renderContentInfo(currentContent, formatDate, formatFileSize)}

              {/* Mobile Badges - Below Size for mobile only */}
              {renderMobileBadges(
                isVideoContent ? videoTypeLabel : imageTypeLabel,
                currentContent.status
              )}

              {/* Additional info for images and extended videos */}
              {renderContentSpecificInfo(!!isVideoContent, selectedVideo, !!isImageContent, selectedImage)}

              {/* Generation Prompt */}
              {renderPromptInfo(currentContent.prompt, showCopiedMessage, handleCopyPrompt)}

              {/* Feedback - Only show if feedback exists */}
              {currentContent.feedback && currentContent.feedback.trim() !== '' && (
                <div>
                  <span className="block text-gray-600 font-medium mb-1" style={{ fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '18px', lineHeight: '26px', letterSpacing: '0%' }}>Feedback</span>
                  <p className="text-gray-900 rounded-lg p-3 border border-gray-200" style={{ backgroundColor: '#F0F7FF', fontFamily: 'Maven Pro', fontWeight: 400, fontSize: '20px', lineHeight: '100%', letterSpacing: '0%' }}>
                    {currentContent.feedback}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Extend Video Section */}
          {!showDeleteConfirm && isVideoContent && selectedVideo && (
            <div className="px-6 pb-4 border-t border-gray-100 pt-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                Extend video length
              </p>
              <div className="flex items-center justify-between gap-3">
                {/* Duration chips */}
                <div className="flex items-center gap-1.5">
                  {[
                    { value: 30, label: '30s' },
                    { value: 60, label: '1m' },
                    { value: 90, label: '1:30' },
                    { value: 120, label: '2m' },
                    { value: 150, label: '2:30' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setExtensionDuration(value)}
                      disabled={isExtendingVideo}
                      className="px-3 py-1 text-xs font-medium rounded-full border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      style={
                        extensionDuration === value
                          ? {
                              background: 'linear-gradient(to right, #F69A7E, #939BEC)',
                              color: '#fff',
                              borderColor: 'transparent',
                            }
                          : {
                              background: '#fff',
                              color: '#6b7280',
                              borderColor: '#e5e7eb',
                            }
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Action button */}
                <button
                  onClick={handleExtendVideo}
                  disabled={isDownloadingFrame || isDownloadingVideo || isExtendingVideo}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 whitespace-nowrap"
                  style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}
                >
                  {isExtendingVideo ? (
                    <>
                      {renderLoadingSpinner('white')}
                      <span>{isAdmin ? 'Starting…' : 'Submitting…'}</span>
                    </>
                  ) : (
                    <>
                      <VideoLibrary style={{ fontSize: '14px' }} />
                      <span>
                        {isAdmin
                          ? `Extend to ${formatDuration(extensionDuration)}`
                          : `Request ${formatDuration(extensionDuration)}`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Modal Footer with Buttons */}
          <div className="px-6 pb-6 pt-2">
            {(() => {
              const footerProps: FooterButtonsProps = {
                showDeleteConfirm,
                isVideoContent: Boolean(isVideoContent),
                isImageContent: Boolean(isImageContent),
                selectedVideo,
                selectedImage,
                isDownloadingFrame,
                isDownloadingVideo,
                isExtendingVideo,
                isAdmin,
                hasDeleteRequest,
                onDownloadLastFrame,
                onDelete,
                onImageDelete,
                onRequestDelete,
                onRequestImageDelete,
                handleDownloadVideo,
                handleDownloadFrame,
                onClose,
                setShowDeleteConfirm,
                onCopyLink: handleCopyLink,
                linkCopied,
              };
              return (
                <>
                  {/* Mobile Layout: 2 rows */}
                  <MobileFooterButtons {...footerProps} />
                  {/* Desktop Layout: Original single row */}
                  <DesktopFooterButtons {...footerProps} />
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContentDetailsModal;